import type { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import type { WorkshopJob } from "../types/garage/atelier";
import { blockSegmentOnDate } from "@utils/blockSegments";

/**
 * Présence vs charge d'un compagnon.
 *
 * Présence = heures posées dans une zone productive (mêmes zones que la capacité
 * réception : l'administratif est du temps travaillé mais pas de la capacité
 * atelier), moins les congés et les pauses qui les recouvrent.
 * Charge = durée des jobs posés sur le compagnon ce jour-là.
 */
const CAPACITY_ZONES: ZoneType[] = [
  "mecanique",
  "carrosserie",
  "peinture",
  "diagnostique",
  "service_rapide",
  "campagnes",
];

type Interval = [number, number];

function merge(intervals: Interval[]): Interval[] {
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  const out: Interval[] = [];
  for (const [s, e] of sorted) {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

function subtract(base: Interval[], holes: Interval[]): Interval[] {
  let segs = base;
  for (const [hs, he] of holes) {
    const next: Interval[] = [];
    for (const [s, e] of segs) {
      if (he <= s || hs >= e) { next.push([s, e]); continue; }
      if (hs > s) next.push([s, hs]);
      if (he < e) next.push([he, e]);
    }
    segs = next;
  }
  return segs;
}

/** Heures de présence productive d'un compagnon sur une date ISO. */
export function workerPresenceHours(blocks: AvailabilityBlock[], workerId: string, date: string): number {
  const work: Interval[] = [];
  const off: Interval[] = [];
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    const zone = (b.zoneType as ZoneType) || "mecanique";
    if (zone === "conge" || zone === "pause") off.push([seg.startHour, seg.endHour]);
    else if (CAPACITY_ZONES.includes(zone)) work.push([seg.startHour, seg.endHour]);
  }
  return subtract(merge(work), off).reduce((sum, [s, e]) => sum + (e - s), 0);
}

/** Heures chargées (jobs posés) d'un compagnon sur une date ISO. */
export function workerLoadHours(jobs: WorkshopJob[], workerId: string, date: string): number {
  let sum = 0;
  for (const j of jobs) {
    if (j.workerId === workerId && j.date === date) sum += j.durationHours || 0;
  }
  return sum;
}
